import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ReportChart, ReportColumn, ResultCell } from "../../../api/portalReportsApi";
import { cellNumber, formatCell, formatValue } from "./formatCell";

/** Brand primary first, then a fixed palette that reads well next to it (24 distinct hues before it repeats). */
const PALETTE = [
  "var(--portal-primary)", "#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#0ea5e9", "#f97316",
  "#14b8a6", "#e11d48", "#64748b", "#a3e635", "#7c3aed", "#0d9488", "#dc2626", "#ca8a04",
  "#1d4ed8", "#db2777", "#4d7c0f", "#9333ea", "#0891b2", "#b45309", "#334155", "#be123c",
];

/** Rows shown in a tooltip before the rest is folded into "+n more". */
const TOOLTIP_MAX_ROWS = 12;

interface TooltipEntry {
  name?: string | number;
  value?: number | string;
  color?: string;
}

/**
 * Tooltip that reads well with many series: only non-zero series, largest
 * first, each with its colour and share, plus the total for the category.
 */
function ChartTooltip({ active, payload, label, fmt }: { active?: boolean; payload?: TooltipEntry[]; label?: string | number; fmt: (v: unknown) => string }) {
  if (!active || !payload || payload.length === 0) return null;
  const entries = payload
    .map((p) => ({ name: String(p.name ?? ""), value: Number(p.value ?? 0), color: p.color ?? "var(--portal-muted)" }))
    .filter((p) => p.value !== 0)
    .sort((a, b) => b.value - a.value);
  const total = entries.reduce((a, e) => a + e.value, 0);
  const shown = entries.slice(0, TOOLTIP_MAX_ROWS);
  const rest = entries.length - shown.length;
  return (
    <div style={{ background: "white", border: "1px solid var(--portal-border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.14)", padding: "8px 10px", fontSize: 12, minWidth: 180, maxWidth: 320 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: "var(--portal-text)" }}>{label}</div>
      {entries.length === 0 && <div style={{ color: "var(--portal-muted)" }}>No calls</div>}
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <tbody>
          {shown.map((e) => (
            <tr key={e.name}>
              <td style={{ padding: "1px 6px 1px 0", whiteSpace: "nowrap" }}>
                <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: e.color, marginRight: 6, verticalAlign: "middle" }} />
                <span style={{ color: "var(--portal-text)" }}>{e.name}</span>
              </td>
              <td style={{ padding: "1px 0", textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmt(e.value)}</td>
              <td style={{ padding: "1px 0 1px 8px", textAlign: "right", color: "var(--portal-muted)", whiteSpace: "nowrap" }}>{total > 0 ? `${Math.round((e.value / total) * 100)}%` : ""}</td>
            </tr>
          ))}
          {rest > 0 && (
            <tr>
              <td colSpan={3} style={{ padding: "2px 0", color: "var(--portal-muted)" }}>+{rest} more</td>
            </tr>
          )}
          {entries.length > 1 && (
            <tr>
              <td style={{ padding: "4px 6px 0 0", borderTop: "1px solid var(--portal-border)", fontWeight: 700 }}>Total</td>
              <td style={{ padding: "4px 0 0", borderTop: "1px solid var(--portal-border)", textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(total)}</td>
              <td style={{ borderTop: "1px solid var(--portal-border)" }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export interface ChartData {
  categories: { category: string; [series: string]: string | number }[];
  series: string[];
}

function aggregate(values: number[], how: ReportChart["aggregate"]): number {
  if (values.length === 0) return 0;
  switch (how) {
    case "count":
      return values.length;
    case "avg":
      return values.reduce((a, b) => a + b, 0) / values.length;
    case "min":
      return Math.min(...values);
    case "max":
      return Math.max(...values);
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

/**
 * Pivots result rows into chart rows: one entry per category, one key per
 * series (from the series column's distinct values, or one per y column),
 * aggregated, optionally sorted and cut to the top N (rest → "Other").
 */
export function buildChartData(rows: ResultCell[][], colIndex: Record<string, number>, columns: ReportColumn[], chart: ReportChart): ChartData {
  const catIdx = chart.categoryColumn ? colIndex[chart.categoryColumn] : undefined;
  if (catIdx == null) return { categories: [], series: [] };
  const catCol = columns.find((c) => c.name === chart.categoryColumn);
  const seriesIdx = chart.seriesColumn ? colIndex[chart.seriesColumn] : undefined;
  const seriesCol = columns.find((c) => c.name === chart.seriesColumn);
  const valueIdx = chart.valueColumn ? colIndex[chart.valueColumn] : undefined;
  const yCols = (chart.yColumns ?? []).filter((y) => colIndex[y] != null);

  // category → series → raw values
  const buckets = new Map<string, Map<string, number[]>>();
  const seriesOrder: string[] = [];
  const addValue = (cat: string, series: string, v: number | null) => {
    if (v == null) return;
    let s = buckets.get(cat);
    if (!s) buckets.set(cat, (s = new Map()));
    let arr = s.get(series);
    if (!arr) {
      s.set(series, (arr = []));
    }
    if (!seriesOrder.includes(series)) seriesOrder.push(series);
    arr.push(v);
  };
  for (const r of rows) {
    const rawCat = r[catIdx];
    const cat = rawCat == null || rawCat === "" ? "(empty)" : formatCell(rawCat, catCol) || String(rawCat);
    if (seriesIdx != null) {
      const rawS = r[seriesIdx];
      const s = rawS == null || rawS === "" ? "(empty)" : formatCell(rawS, seriesCol) || String(rawS);
      addValue(cat, s, valueIdx != null ? cellNumber(r[valueIdx]) : 1);
    } else if (yCols.length > 0) {
      for (const y of yCols) addValue(cat, columns.find((c) => c.name === y)?.label || y, cellNumber(r[colIndex[y]]));
    } else if (valueIdx != null) {
      addValue(cat, columns.find((c) => c.name === chart.valueColumn)?.label || chart.valueColumn || "value", cellNumber(r[valueIdx]));
    }
  }

  // Fixed numeric range (e.g. 1..21 entries per entrant): every integer is a
  // category, missing ones count 0, and the axis is ordered numerically.
  if (chart.categoryFrom != null || chart.categoryTo != null) {
    const nums = [...buckets.keys()].map(Number).filter((n) => Number.isFinite(n));
    const from = chart.categoryFrom ?? (nums.length ? Math.min(...nums) : 0);
    const to = Math.max(chart.categoryTo ?? from, nums.length ? Math.max(...nums) : from);
    if (to - from <= 2000) {
      for (let n = from; n <= to; n += 1) {
        const key = String(n);
        if (!buckets.has(key)) buckets.set(key, new Map());
      }
      for (const s of buckets.values()) for (const series of seriesOrder) if (!s.has(series)) s.set(series, [0]);
    }
  }

  let categories = [...buckets.entries()].map(([category, s]) => {
    const row: ChartData["categories"][number] = { category };
    let total = 0;
    for (const series of seriesOrder) {
      const v = aggregate(s.get(series) ?? [], chart.aggregate ?? "sum");
      row[series] = v;
      total += v;
    }
    return { row, total };
  });
  if (chart.sort === "value") categories.sort((a, b) => b.total - a.total);
  else if (chart.sort === "category" || chart.categoryFrom != null || chart.categoryTo != null) {
    const numeric = categories.every((c) => Number.isFinite(Number(c.row.category)));
    categories.sort((a, b) => (numeric ? Number(a.row.category) - Number(b.row.category) : a.row.category.localeCompare(b.row.category)));
  }
  if (chart.topN && chart.topN > 0 && categories.length > chart.topN) {
    const head = categories.slice(0, chart.topN);
    const rest = categories.slice(chart.topN);
    const other: ChartData["categories"][number] = { category: "Other" };
    for (const series of seriesOrder) other[series] = rest.reduce((a, c) => a + Number(c.row[series] ?? 0), 0);
    categories = [...head, { row: other, total: rest.reduce((a, c) => a + c.total, 0) }];
  }
  return { categories: categories.map((c) => c.row), series: seriesOrder };
}

export function ChartSection({ chart, columns, colIndex, rows }: { chart: ReportChart; columns: ReportColumn[]; colIndex: Record<string, number>; rows: ResultCell[][] }) {
  const data = useMemo(() => buildChartData(rows, colIndex, columns, chart), [rows, colIndex, columns, chart]);
  const valueCol = columns.find((c) => c.name === chart.valueColumn) ?? columns.find((c) => c.name === chart.yColumns?.[0]);
  const fmt = (v: unknown) => formatValue(typeof v === "number" ? v : Number(v), chart.valueFormat ?? valueCol?.format ?? "integer", valueCol?.decimals);
  const height = chart.horizontal ? Math.max(260, Math.min(900, data.categories.length * 26 + 60)) : 320;

  if (data.categories.length === 0 || data.series.length === 0) {
    return <div style={{ color: "var(--portal-muted)" }}>Nothing to chart.</div>;
  }

  if (chart.kind === "pie") {
    const pieData = data.series.length === 1
      ? data.categories.map((c) => ({ name: c.category, value: Number(c[data.series[0]] ?? 0) }))
      : data.series.map((s) => ({ name: s, value: data.categories.reduce((a, c) => a + Number(c[s] ?? 0), 0) }));
    return (
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius="80%" label={(e) => `${e.name}: ${fmt(e.value)}`}>
              {pieData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip fmt={fmt} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const axisProps = { tick: { fontSize: 11 }, stroke: "var(--portal-muted)" };
  // Whole numbers on the value axis, and at least `yMax` high when configured.
  const valueDomain: [number, (dataMax: number) => number] = [0, (dataMax: number) => Math.max(dataMax, chart.yMax ?? 0)];
  const common = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="var(--portal-border)" />
      {chart.horizontal ? (
        <>
          <XAxis type="number" {...axisProps} tickFormatter={(v) => fmt(v)} domain={valueDomain} allowDecimals={false} />
          <YAxis type="category" dataKey="category" {...axisProps} width={160} interval={0} />
        </>
      ) : (
        <>
          <XAxis dataKey="category" {...axisProps} interval={0} angle={data.categories.length > 12 ? -35 : 0} textAnchor={data.categories.length > 12 ? "end" : "middle"} height={data.categories.length > 12 ? 80 : 30} />
          <YAxis {...axisProps} tickFormatter={(v) => fmt(v)} domain={valueDomain} allowDecimals={false} />
        </>
      )}
      <Tooltip content={<ChartTooltip fmt={fmt} />} cursor={{ fill: "rgba(15, 23, 42, 0.06)" }} />
      {data.series.length > 1 && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconSize={10} />}
    </>
  );

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        {chart.kind === "line" ? (
          <LineChart data={data.categories} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            {common}
            {data.series.map((s, i) => (
              <Line key={s} type="monotone" dataKey={s} stroke={PALETTE[i % PALETTE.length]} dot={data.categories.length <= 40} strokeWidth={2} />
            ))}
          </LineChart>
        ) : (
          <BarChart data={data.categories} layout={chart.horizontal ? "vertical" : "horizontal"} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            {common}
            {data.series.map((s, i) => (
              <Bar key={s} dataKey={s} stackId={chart.stacked ? "a" : undefined} fill={PALETTE[i % PALETTE.length]} radius={chart.stacked ? 0 : 3} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
